import { useState } from 'react';

import { Breadcrumbs, Button, Title, toastService } from '@thefarmersfront/kitchen';

import {
  VenderConfirmDefaultInfoSection,
  VenderConfirmLinkedInfoSection,
  VenderConfirmLogisticsInfoSection,
  VenderConfirmManagerInfoSection,
  VenderPasswordConfirmModal,
} from '~/components/vender';

import { useModal } from '~/hooks/common';
import { usePatchOwner } from '~/hooks/vender';
import { useUserInfoStore } from '~/store';
import { validateTelFormat } from '~/utils';

import {
  BREAD_CRUMB,
  DEFAULT_INPUT_STATUS,
  ERROR_INVALID_FORMAT_INPUT_STATUS,
  ERROR_REQUIRED_INPUT_STATUS,
  PATH,
  TOAST_MESSAGE,
} from '~/constants';

import type { TypeInputState } from '~/types/common.types';
import type { TypeOwnerDetail, TypeOwnerManagerDetail } from '~/types/vender.types';

import styles from './VenderConfirmPage.module.scss';

export type TypeDefaultInfoValue = {
  businessConditions: string;
  businessCategory: string;
  zipCode: string;
  address: string;
  detailAddress: string;
  tel: string;
  fax: string;
};

export type TypeDefaultInfoValueState = {
  businessConditions: TypeInputState;
  businessCategory: TypeInputState;
  tel: TypeInputState;
  fax: TypeInputState;
};

export type TypeLinkedInfoValue = {
  nfaReturnExchangeLinkYn: boolean | null;
};

const DEFAULT_DEFAULT_INFO_VALUE: TypeDefaultInfoValue = {
  businessConditions: '',
  businessCategory: '',
  zipCode: '',
  address: '',
  detailAddress: '',
  tel: '',
  fax: '',
};

/**
 * [화주사 정보 관리 > 화주사 정보] 페이지
 */
const VenderConfirmPage = () => {
  const {
    userInfo: { userClass },
  } = useUserInfoStore();

  const hasEditPermission = userClass === 'MASTER';

  const { updateOwner } = usePatchOwner();

  const hasPermission = useUserInfoStore((state) => state.hasPermission);
  const hasNFAPermission = hasPermission([{ ownerType: 'NFA' }]);
  const hasNFAFulfillmentPermission = hasPermission([{ ownerType: 'NFA', logisticsTypes: ['FULFILLMENT'] }]);

  const [venderInfo, setVenderInfo] = useState<TypeOwnerDetail | null>(null);
  const [managerInfo, setManagerInfo] = useState<TypeOwnerManagerDetail[]>([]);

  const [defaultInfoValue, setDefaultInfoValue] = useState(DEFAULT_DEFAULT_INFO_VALUE);
  const [defaultInfoValueState, setDefaultInfoValueState] = useState<TypeDefaultInfoValueState>({
    businessConditions: DEFAULT_INPUT_STATUS,
    businessCategory: DEFAULT_INPUT_STATUS,
    tel: DEFAULT_INPUT_STATUS,
    fax: DEFAULT_INPUT_STATUS,
  });

  const [linkedInfoValue, setLinkedInfoValue] = useState<TypeLinkedInfoValue>({
    nfaReturnExchangeLinkYn: null,
  });

  const { isOpen: isPasswordConfirmModalOpen, handleConfirmModal: handleConfirmPasswordConfirmModal } = useModal({
    defaultOpen: true,
    onConfirm: (data?: TypeOwnerDetail) => {
      if (!data) return;
      setVenderInfo(data);
      setDefaultInfoValue({
        ...defaultInfoValue,
        businessConditions: data.businessConditions,
        businessCategory: data.businessCategory,
        zipCode: data.zipCode,
        address: data.address,
        detailAddress: data.detailAddress,
        tel: data.tel,
        fax: data.fax,
      });
      setLinkedInfoValue({
        nfaReturnExchangeLinkYn: data.nfaReturnExchangeLinkYn ?? null,
      });
      setManagerInfo(data.ownerManagerDetailDomainList);
    },
  });

  const validateForm = (): boolean => {
    let isValidForm = true;

    if (defaultInfoValue.businessConditions === '') {
      setDefaultInfoValueState((prev) => ({ ...prev, businessConditions: ERROR_REQUIRED_INPUT_STATUS }));
      isValidForm = false;
    }
    if (defaultInfoValue.businessCategory === '') {
      setDefaultInfoValueState((prev) => ({ ...prev, businessCategory: ERROR_REQUIRED_INPUT_STATUS }));
      isValidForm = false;
    }
    if (defaultInfoValue.tel === '') {
      setDefaultInfoValueState((prev) => ({ ...prev, tel: ERROR_REQUIRED_INPUT_STATUS }));
      isValidForm = false;
    } else if (!validateTelFormat(defaultInfoValue.tel)) {
      setDefaultInfoValueState((prev) => ({ ...prev, tel: ERROR_INVALID_FORMAT_INPUT_STATUS }));
      isValidForm = false;
    }
    if (defaultInfoValue.fax !== '' && !validateTelFormat(defaultInfoValue.fax)) {
      setDefaultInfoValueState((prev) => ({ ...prev, fax: ERROR_INVALID_FORMAT_INPUT_STATUS }));
      isValidForm = false;
    }

    return isValidForm;
  };

  const handleSave = () => {
    const isValidForm = validateForm();

    if (isValidForm) {
      updateOwner(
        { ...defaultInfoValue, ...linkedInfoValue },
        {
          onSuccess: () => {
            toastService.addToast({ message: '성공적으로 저장되었습니다.' });
          },
          onError: (error) => {
            toastService.addToast({
              type: 'error',
              message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
            });
          },
        }
      );
    }
  };

  return (
    <div className={styles['page-wrapper']}>
      <div className={styles['title-section']}>
        <Title size="accessibility1">화주사 정보</Title>
        <Breadcrumbs items={BREAD_CRUMB[PATH.VENDER_CONFIRM]} />
      </div>
      {isPasswordConfirmModalOpen ? (
        <VenderPasswordConfirmModal open={isPasswordConfirmModalOpen} onConfirm={handleConfirmPasswordConfirmModal} />
      ) : (
        <>
          <VenderConfirmDefaultInfoSection
            hasEditPermission={hasEditPermission}
            venderInfo={venderInfo}
            defaultInfoValue={defaultInfoValue}
            setDefaultInfoValue={setDefaultInfoValue}
            defaultInfoValueState={defaultInfoValueState}
            setDefaultInfoValueState={setDefaultInfoValueState}
          />
          {hasNFAPermission && <VenderConfirmLogisticsInfoSection venderInfo={venderInfo} />}
          {hasNFAFulfillmentPermission && (
            <VenderConfirmLinkedInfoSection
              hasEditPermission={hasEditPermission}
              linkedInfoValue={linkedInfoValue}
              setLinkedInfoValue={setLinkedInfoValue}
            />
          )}
          <VenderConfirmManagerInfoSection
            hasEditPermission={hasEditPermission}
            managerInfo={managerInfo}
            setManagerInfo={setManagerInfo}
          />
          <div className={styles['page-bottom-section']}></div>
          {hasEditPermission && <VenderConfirmControlSection handleSaveClick={handleSave} />}
        </>
      )}
    </div>
  );
};

const VenderConfirmControlSection = ({ handleSaveClick }: { handleSaveClick: () => void }) => {
  return (
    <div className={styles['control-section']}>
      <Button size="medium" onClick={handleSaveClick}>
        저장
      </Button>
    </div>
  );
};

export default VenderConfirmPage;
